// City data for /pub-crawl/:slug landing pages.
// Each city is a long-tail SEO target: "pub crawl <city>", "bar crawl <city>",
// "kneipentour <city>". Keep slugs lowercase, hyphenated, ASCII.

export interface City {
  slug: string;
  name: string;
  nameDe?: string;
  country: string;
  intro: string;
  neighborhoods: string[];
  vibe: string;
  titleOverride?: string;
  descriptionOverride?: string;
  routeAdvice?: { heading: string; text: string }[];
}

export const cities: City[] = [
  {
    slug: "cologne",
    name: "Cologne",
    nameDe: "Köln",
    country: "Germany",
    intro:
      "Explore Cologne's Kneipen and Kölsch pubs by choosing a neighborhood as your starting point. Keep the route in one area to spend more of the evening at venues and less time crossing the city.",
    neighborhoods: ["Altstadt", "Belgisches Viertel", "Ehrenfeld", "Südstadt", "Zülpicher Viertel"],
    vibe: "classic German Kneipe culture, Kölsch beer halls and student bars",
  },
  {
    slug: "berlin",
    name: "Berlin",
    country: "Germany",
    intro:
      "Plan a Berlin bar crawl around one neighborhood, such as Friedrichshain, Kreuzberg or Neukölln. Choose a starting point and compare the suggested walking routes before heading out.",
    neighborhoods: ["Kreuzberg", "Friedrichshain", "Neukölln", "Mitte", "Prenzlauer Berg"],
    vibe: "dive bars, Spätis, craft cocktails and 24-hour energy",
  },
  {
    slug: "hamburg",
    name: "Hamburg",
    country: "Germany",
    intro:
      "Choose St. Pauli or the Schanzenviertel as a starting area for a Hamburg pub crawl. A neighborhood-based route helps keep the evening focused instead of moving between distant parts of the city.",
    neighborhoods: ["St. Pauli", "Schanzenviertel", "Reeperbahn", "Sternschanze", "Karoviertel"],
    vibe: "harbor pubs, Reeperbahn bars and indie Schanzen spots",
  },
  {
    slug: "munich",
    name: "Munich",
    nameDe: "München",
    country: "Germany",
    intro:
      "Munich pub crawls run on Helles, Weizen and Gemütlichkeit. Build a bar tour through Glockenbachviertel for cocktails, Schwabing for student bars, or the Altstadt for classic Bavarian beer halls.",
    neighborhoods: ["Altstadt", "Glockenbachviertel", "Schwabing", "Maxvorstadt", "Haidhausen"],
    vibe: "beer halls, beer gardens and stylish cocktail bars",
  },
  {
    slug: "frankfurt",
    name: "Frankfurt",
    country: "Germany",
    intro:
      "Frankfurt's bar scene is split between Sachsenhausen's Apfelwein taverns and Bahnhofsviertel's cocktail bars. A pub crawl route generator picks the best stops in each and keeps the walk short.",
    neighborhoods: ["Sachsenhausen", "Bahnhofsviertel", "Bornheim", "Nordend", "Innenstadt"],
    vibe: "Apfelwein taverns, cocktail bars and after-work spots",
  },
  {
    slug: "dusseldorf",
    name: "Düsseldorf",
    country: "Germany",
    intro:
      "Düsseldorf's Altstadt is famously known as 'die längste Theke der Welt' — the longest bar in the world. Hundreds of pubs in a few blocks make it the perfect spot for a tightly timed pub crawl.",
    neighborhoods: ["Altstadt", "Flingern", "Bilk", "Unterbilk", "Pempelfort"],
    vibe: "Altbier brewpubs, longest-bar-in-the-world energy",
    titleOverride: "Düsseldorf Pub Crawl Map & Route Planner | Golden Mile",
    descriptionOverride: "Plan a Düsseldorf pub crawl in the Altstadt or another neighborhood. Create a walking map with venue hours and a schedule, then share via Google Maps.",
    routeAdvice: [
      { heading: "Altstadt pub crawl", text: "For an Altstadt route, enter Düsseldorf Altstadt rather than the whole city. Compare nearby pub stops and walking times on the generated map; it is a suggested route, not a brewery booking." },
      { heading: "Flingern or Bilk", text: "For a different starting area, search Flingern or Bilk separately. Pick one neighborhood rather than combining all three into a single walk, and check the total walking time before choosing a route." },
    ],
  },
  {
    slug: "dresden-neustadt",
    name: "Dresden Neustadt",
    country: "Germany",
    intro:
      "Start a Dresden Neustadt pub crawl around Alaunstraße, Louisenstraße or Görlitzer Straße. Choose a starting area and compare nearby venues on a walking map.",
    neighborhoods: ["Äußere Neustadt", "Innere Neustadt", "Alaunstraße", "Louisenstraße", "Görlitzer Straße"],
    vibe: "indie bars, student Kneipen and late-night dives",
  },
  {
    slug: "leipzig",
    name: "Leipzig",
    country: "Germany",
    intro:
      "Leipzig's Karli (Karl-Liebknecht-Straße) and Plagwitz are bar crawl gold. Cheap drinks, walkable distances and a relaxed atmosphere make it one of Germany's best cities for a long pub crawl.",
    neighborhoods: ["Südvorstadt", "Plagwitz", "Connewitz", "Zentrum", "Lindenau"],
    vibe: "alternative bars, beer gardens and student-priced drinks",
  },
  {
    slug: "vienna",
    name: "Vienna",
    nameDe: "Wien",
    country: "Austria",
    intro:
      "Vienna's Bermudadreieck (Bermuda Triangle) and the Gürtel are made for bar hopping. From wine bars to dive bars, a planned pub crawl route covers more ground than wandering ever will.",
    neighborhoods: ["Bermudadreieck", "Naschmarkt", "Spittelberg", "Gürtel", "Neubau"],
    vibe: "wine bars, beisl pubs and the famous Bermudadreieck",
  },
  {
    slug: "zurich",
    name: "Zurich",
    nameDe: "Zürich",
    country: "Switzerland",
    intro:
      "Zurich's Langstrasse and Niederdorf are dense with bars, from craft cocktail rooms to neighborhood Kneipen. A pub crawl generator is the easy way to plan a night that doesn't blow the budget on the first stop.",
    neighborhoods: ["Langstrasse", "Niederdorf", "Kreis 4", "Kreis 5", "Altstadt"],
    vibe: "craft cocktails, dive bars and lakeside terraces",
  },
  {
    slug: "london",
    name: "London",
    country: "United Kingdom",
    intro:
      "London is the spiritual home of the pub crawl. From Soho to Shoreditch and Camden, there are thousands of pubs — a pub crawl planner is the only sane way to pick a route through them.",
    neighborhoods: ["Soho", "Shoreditch", "Camden", "Borough", "Notting Hill"],
    vibe: "historic pubs, gastropubs and East London cocktail bars",
    titleOverride: "London Pub Crawl Map & Free Route Planner | Golden Mile",
    descriptionOverride: "Create a London pub crawl map around Soho, Shoreditch or Camden. Compare walking routes, venue hours and schedules, then share a Google Maps link.",
    routeAdvice: [
      { heading: "Soho or Camden", text: "For a central London route, enter Soho as your starting area. For a Camden pub crawl, search Camden instead. Generate each separately and compare the walking times rather than treating the whole city as one route." },
      { heading: "East London pub crawl", text: "Start with Shoreditch if you want an East London bar crawl. Use a neighborhood or a specific pub as your starting point, then check the map before choosing a route. Golden Mile generates a custom walk, not a fixed Monopoly pub crawl." },
    ],
  },
  {
    slug: "dublin",
    name: "Dublin",
    country: "Ireland",
    intro:
      "Choose Temple Bar, the Liberties, Camden Street or Stoneybatter as a starting area for a Dublin pub crawl. Plan one neighborhood at a time and check walking times between the suggested pubs.",
    neighborhoods: ["Temple Bar", "The Liberties", "Camden Street", "Stoneybatter", "Portobello"],
    vibe: "traditional Irish pubs, live music and Guinness",
  },
  {
    slug: "amsterdam",
    name: "Amsterdam",
    country: "Netherlands",
    intro:
      "Amsterdam's brown cafés (bruine kroegen) are perfect bar crawl stops. A planned route through the Jordaan and De Pijp keeps walking distances short and canals as a bonus.",
    neighborhoods: ["Jordaan", "De Pijp", "Centrum", "Oost", "Westerpark"],
    vibe: "brown cafés, canal-side terraces and craft beer bars",
  },
  {
    slug: "barcelona",
    name: "Barcelona",
    country: "Spain",
    intro:
      "Barcelona bar crawls run late. From Gòtic's vermouth bars to Gràcia's plazas and El Born's cocktail spots, a planned pub crawl route keeps the night flowing from tapas to cocktails to club.",
    neighborhoods: ["El Born", "Gràcia", "El Raval", "Gòtic", "Poble-sec"],
    vibe: "vermouth bars, tapas spots and late-night cocktail rooms",
  },
  {
    slug: "prague",
    name: "Prague",
    nameDe: "Prag",
    country: "Czechia",
    intro:
      "Prague is one of Europe's cheapest cities for a pub crawl. Beer is often cheaper than water and the Old Town, Žižkov and Vinohrady are packed with great pubs within walking distance.",
    neighborhoods: ["Žižkov", "Vinohrady", "Staré Město", "Karlín", "Holešovice"],
    vibe: "Czech beer halls, dive bars and underground cocktail rooms",
  },
  {
    slug: "new-york",
    name: "New York",
    country: "USA",
    intro:
      "New York bar crawls work best one neighborhood at a time. The East Village, Lower East Side, Williamsburg and Bushwick each have enough bars in a few blocks to fill an entire night.",
    neighborhoods: ["East Village", "Lower East Side", "Williamsburg", "Bushwick", "West Village"],
    vibe: "dive bars, speakeasies and rooftop cocktails",
  },
  {
    slug: "blackpool",
    name: "Blackpool",
    country: "United Kingdom",
    intro:
      "Planning a Blackpool Golden Mile pub crawl? Choose a starting pub or a location near the Promenade and compare suggested nearby stops on a walking map. Golden Mile is a self-guided planning app, not a Blackpool pub or a ticketed tour operator; it does not provide a fixed official Golden Mile itinerary.",
    neighborhoods: ["Promenade / Golden Mile", "Talbot Road", "Church Street", "South Shore", "Bispham"],
    vibe: "the original British pub crawl strip — seaside pubs, karaoke bars and stag-do classics",
    titleOverride: "Blackpool Golden Mile Pub Crawl Map | Golden Mile",
    descriptionOverride: "Plan a self-guided Blackpool Golden Mile pub crawl. Choose a starting point near the Promenade, create a walking map and share your route in Google Maps.",
    routeAdvice: [
      { heading: "Blackpool pubs map", text: "Enter Blackpool Promenade or choose a specific starting pub to focus your map on that area. The result depends on nearby venues and available opening hours; it is not a complete directory of Blackpool pubs." },
      { heading: "A self-guided Golden Mile route", text: "Compare the generated stops and walking time before sharing the route. There is no included guide, drinks package or admission. Confirm venue opening hours and group entry directly, especially for a stag or hen party." },
    ],
  },
];

export const getCityBySlug = (slug: string) => cities.find((c) => c.slug === slug);
